import debounce from 'debounce-promise';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { AsyncSelect, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';
import type { IListParams } from '@feathr/rachis';

type TSortDirection = 'asc' | 'desc';

interface IBaseProps {
  /** The text used as the label for the component. */
  label?: ReactNode;
  placeholder?: string;
  /**
   * Whether or not to render a label along with the select component.
   * There are instances where a design requires an alternate implementation of the label, e.g. when grouped with other components.
   */
  displayLabel?: boolean;
  /** Sort direction for groups by date created */
  sortDirection?: TSortDirection;
  filters?: Record<string, unknown>;
  isMulti?: boolean;
}

interface ISingleProps extends IBaseProps {
  value?: string;
  values?: never;
  onChange: (id: string) => void;
  isMulti?: never;
}

interface IMultiProps extends IBaseProps {
  value?: never;
  values?: string[];
  onChange: (ids: string[]) => void;
}

function GroupSelect(props: ISingleProps): JSX.Element;
function GroupSelect(props: IMultiProps): JSX.Element;
function GroupSelect({
  label,
  displayLabel = true,
  placeholder,
  value,
  values,
  sortDirection = 'asc',
  onChange,
  isMulti = false,
  filters = {},
}: ISingleProps | IMultiProps): JSX.Element {
  const { t } = useTranslation();
  const { Segments: Groups } = useStore();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<ISelectOption[]>([]);
  const defaultPlaceholder = isMulti ? t('All groups') : t('Select a group');

  const debouncedGroupOptions = debounce(getGroupOptions, DEFAULT_DEBOUNCE_WAIT);

  async function getGroupOptions(inputValue?: string): Promise<ISelectOption[]> {
    // Newest groups first
    const ordering = [(sortDirection === 'asc' ? '' : '-') + 'date_created'];

    setIsLoading(true);
    try {
      const params: IListParams<Segment> = {
        filters: {
          ...filters,
          parent_segment__exists: false,
        },
        ordering: ordering,
        only: ['id', 'name', 'date_created'],
      };
      if (inputValue?.length) {
        params.filters!.name__icontains = inputValue;
      }
      const groups = Groups.list(params);
      await when(() => !groups.isPending);
      const groupOptions = groups.models.map(({ id, name }) => ({
        id,
        name,
      }));
      setOptions(groupOptions);
      return groupOptions;
    } catch {
      toast(t("Couldn't load groups. Try again."), { type: 'error' });
      return [];
    } finally {
      setIsLoading(false);
    }
  }

  // TODO: single group select interface is colliding with multi group select.
  function handleSingleGroupSelect({
    onChange,
  }: ISingleProps): ({ id }: ISelectOption) => Promise<void> {
    return async ({ id }: ISelectOption): Promise<void> => {
      if (typeof id !== 'undefined') {
        onChange(id);
      }
      if (id === undefined) {
        /*
         * AsyncSelect makes us re-fetch the options when typing, selecting,
         * and then clearing the input.
         */
        const newOptions = await getGroupOptions();
        setOptions(newOptions);
      }
    };
  }

  function handleMultiGroupSelect({
    onChange,
    isMulti,
  }: IMultiProps): (values: ISelectOption[]) => Promise<void> {
    return async (values: ISelectOption[]): Promise<void> => {
      const isArray = Array.isArray(values);
      if (values !== undefined && isArray && isMulti) {
        onChange(values.map(({ id }) => id));
      }
      if (values.length === 0) {
        /*
         * AsyncSelect makes us re-fetch the options when typing, selecting,
         * and then clearing the input.
         */
        const newOptions = await getGroupOptions();
        setOptions(newOptions);
      }
    };
  }

  function getOptionLabel({ name }: ISelectOption): string {
    return name;
  }

  function getOptionValue({ id }: ISelectOption): string {
    return id;
  }

  function getLabel(): ReactNode | undefined {
    const defaultLabel = isMulti ? t('Groups') : t('Group');
    if (!displayLabel) {
      return undefined;
    }
    if (label) {
      return label;
    }
    return !label && defaultLabel;
  }

  return (
    <AsyncSelect
      cacheOptions={true}
      defaultOptions={true}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      isLoading={isLoading}
      isMulti={isMulti}
      label={getLabel()}
      loadOptions={debouncedGroupOptions}
      name={'group-select'}
      onSelectMulti={isMulti && values ? handleMultiGroupSelect({ onChange, isMulti }) : undefined}
      onSelectSingle={value ? handleSingleGroupSelect({ onChange }) : undefined}
      placeholder={placeholder ?? defaultPlaceholder}
      value={
        isMulti
          ? options.filter(({ id }) => values?.includes(id))
          : options.find(({ id }) => id === value)
      }
    />
  );
}

export default observer(GroupSelect);
